import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { isOnboardingCompleted } from '../services/onboardingService';
import GuestBanner from './GuestBanner';

interface RequireAuthProps {
  children: React.ReactNode;
  allowGuest?: boolean;
}

const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen bg-sage-50">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-sage-300 border-t-sage-600 rounded-full animate-spin mx-auto mb-4" />
      <p className="text-sage-600 text-sm font-medium">Checking authentication...</p>
    </div>
  </div>
);

const RequireAuth: React.FC<RequireAuthProps> = ({ children, allowGuest = false }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    if (allowGuest) {
      // Guest mode: render children with a banner prompting sign-up
      return (
        <>
          <GuestBanner />
          {children}
        </>
      );
    }
    // Redirect to login with the current path as return URL
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  // Authenticated but onboarding not complete
  if (!isOnboardingCompleted()) {
    return <Navigate to="/onboarding" replace />;
  }

  // Authenticated and onboarded
  return <>{children}</>;
};

export default RequireAuth;
